/**
 * citationDecorationPlugin — TipTap ProseMirror decoration plugin.
 *
 * Scans the editor document for [N] citation patterns and renders inline
 * decorations styled as clickable citation badges. Segment-aware: each
 * citation is associated with the sectionId of the enclosing segmentHeading
 * so the correct citationRefs can be looked up.
 *
 * Also handles malformed LLM patterns:
 * - [1, 3] or [L3, L7] → decoration over the whole bracket (first index clickable)
 * - [L1 EMP] or [1 Document Name] → decoration with just the numeric index
 *
 * Click handling is done via a DOM event handler — the plugin adds data
 * attributes that the DraftEditor's click handler reads.
 *
 * Every decoration also carries `data-citation-form`: 'marker' for the strict
 * forms the export strips, 'loose' for the malformed matches it keeps. Only
 * 'marker' is hidden by evaluator view — see addDecoration. Each 'marker'
 * also gets a separate `evaluator-strip-space` decoration over the space before
 * it (see evaluatorStripSpaceRange), so the hidden marker leaves no stray space.
 */

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

const CITATION_REGEX = /\[(\d+)\]/g
const LIBRARY_CITATION_REGEX = /\[L(\d+)\]/g

// Malformed patterns — comma-separated: [1, 3] or [1,3,7]
const COMBINED_CITATION_REGEX = /\[(\d+)(?:\s*,\s*\d+)+\]/g
// Malformed patterns — comma-separated library: [L1, L3] or [L1,L3]
const COMBINED_LIBRARY_CITATION_REGEX = /\[L(\d+)(?:\s*,\s*L?\d+)+\]/g
// Malformed patterns — annotated: [1 EMP] or [1 Document Name]
const ANNOTATED_CITATION_REGEX = /\[(\d+)\s+[^\]]+\]/g
// Malformed patterns — annotated library: [L1 EMP]
const ANNOTATED_LIBRARY_CITATION_REGEX = /\[L(\d+)\s+[^\]]+\]/g

/**
 * Every marker shape the editor decorates, in the plugin's priority order —
 * strict forms first, so a malformed match never claims a strict one. Shared
 * with the Full response reader (full-response/citation-markers.ts), which
 * makes the same shapes clickable in stored HTML. `/g` regexes: consume them
 * with `matchAll` (which copies `lastIndex`) or reset `lastIndex` first.
 */
export const CITATION_MARKER_PATTERNS: ReadonlyArray<{
  regex: RegExp
  kind: 'tender' | 'library'
  form: 'marker' | 'loose'
}> = [
  { regex: CITATION_REGEX, kind: 'tender', form: 'marker' },
  { regex: LIBRARY_CITATION_REGEX, kind: 'library', form: 'marker' },
  { regex: COMBINED_CITATION_REGEX, kind: 'tender', form: 'loose' },
  { regex: COMBINED_LIBRARY_CITATION_REGEX, kind: 'library', form: 'loose' },
  { regex: ANNOTATED_CITATION_REGEX, kind: 'tender', form: 'loose' },
  { regex: ANNOTATED_LIBRARY_CITATION_REGEX, kind: 'library', form: 'loose' },
]

export const citationDecorationPluginKey = new PluginKey('citationDecoration')

// Strict citation markers, anchored — used to look past a run of adjacent
// markers ("[1][2].") when deciding whether the space before the first one
// should go.
const LEADING_MARKERS_REGEX = /^(?:\[L?\d+\])+/
// What may follow a hidden token for the space before it to be hidden too:
// another space (collapses to one) or punctuation (no space before it).
const SPACE_COLLAPSES_BEFORE_REGEX = /^[ .,;:!?]/
// Stands in for leaf nodes (hard breaks) so text offsets stay aligned with
// document positions.
const LEAF_PLACEHOLDER = '\uFFFC'

/**
 * Evaluator view (spec 19) hides a token with `display: none` on its own
 * decoration span, which leaves the plain space before it on screen:
 * "work starts [1]. We" would read "work starts . We". This returns the run of
 * spaces immediately before the token [from, to) that should be hidden with it,
 * or null to keep them — mirroring the export's cleanupCitationResidue
 * (backend/src/lib/citation-quotes.ts): drop the space when the token is followed
 * by a space, punctuation or the end of the block, keep it when prose follows
 * directly ("a [1]b" reads "a b").
 *
 * Works on the whole textblock, not the text node, so spaces and following text
 * in a neighbouring mark (bold etc.) are seen. The caller decorates the returned
 * range separately — the token's own decoration must still wrap the token only.
 */
export function evaluatorStripSpaceRange(
  doc: ProseMirrorNode,
  from: number,
  to: number
): { from: number; to: number } | null {
  const $from = doc.resolve(from)
  const block = $from.parent
  const start = $from.start()
  const before = block.textBetween(0, from - start, undefined, LEAF_PLACEHOLDER)
  const spaces = / +$/.exec(before)
  if (!spaces) return null

  const after = block.textBetween(to - start, block.content.size, undefined, LEAF_PLACEHOLDER)
  const rest = after.replace(LEADING_MARKERS_REGEX, '')
  if (rest !== '' && !SPACE_COLLAPSES_BEFORE_REGEX.test(rest)) return null

  return { from: from - spaces[0].length, to: from }
}

/** The decoration evaluator view's CSS hides — `.evaluator-strip-space`. */
export function evaluatorStripSpaceDecoration(
  doc: ProseMirrorNode,
  from: number,
  to: number
): Decoration | null {
  const range = evaluatorStripSpaceRange(doc, from, to)
  if (!range) return null
  return Decoration.inline(range.from, range.to, { class: 'evaluator-strip-space' })
}

export function createCitationDecorationPlugin(): Plugin {
  return new Plugin({
    key: citationDecorationPluginKey,
    props: {
      decorations(state) {
        const decorations: Decoration[] = []
        const { doc } = state

        // First pass: collect segmentHeading positions + sectionIds
        const headings: Array<{ pos: number; sectionId: string }> = []
        doc.descendants((node, pos) => {
          if (node.type.name === 'segmentHeading') {
            headings.push({
              pos,
              sectionId: (node.attrs['sectionId'] as string) ?? '',
            })
          }
        })

        // Helper: find which sectionId a position belongs to
        function getSectionIdAtPos(pos: number): string | null {
          if (headings.length === 0) return null
          let sectionId: string | null = null
          for (const heading of headings) {
            if (heading.pos <= pos) {
              sectionId = heading.sectionId
            } else {
              break
            }
          }
          return sectionId
        }

        // Track which character ranges already have decorations to avoid overlaps
        const decoratedRanges: Array<{ from: number; to: number }> = []

        function isAlreadyDecorated(from: number, to: number): boolean {
          return decoratedRanges.some((r) => from < r.to && to > r.from)
        }

        /**
         * `form` records whether the decorated bracket is one the EXPORT can
         * strip. Export (stripKnownCitationTokens) removes only the exact
         * `[N]` / `[LN]` shapes — 'marker'. Everything else here is a
         * best-effort match on a malformed LLM pattern, and `[30 days]` is
         * indistinguishable from a mangled citation, so those brackets survive
         * into the .docx verbatim — 'loose'.
         *
         * Evaluator view (spec 19) hides only `[data-citation-form="marker"]`.
         * Hiding the loose forms too would turn "within [30 days] of award"
         * into "within of award" in the one view that promises the author an
         * as-exported reading. Pass 'marker' only for the strict patterns.
         */
        function addDecoration(
          from: number,
          to: number,
          citationIndex: number,
          sectionId: string | null,
          form: 'marker' | 'loose',
          citationType?: 'library'
        ) {
          if (isAlreadyDecorated(from, to)) return
          decoratedRanges.push({ from, to })

          const attrs: Record<string, string> = {
            class: 'citation-badge-inline',
            'data-citation-index': String(citationIndex),
            'data-section-id': sectionId ?? '',
            'data-citation-form': form,
            nodeName: 'span',
          }
          if (citationType === 'library') {
            attrs['data-citation-type'] = 'library'
          }
          decorations.push(Decoration.inline(from, to, attrs))

          if (form === 'marker') {
            const strip = evaluatorStripSpaceDecoration(doc, from, to)
            if (strip) decorations.push(strip)
          }
        }

        // Second pass: find citation patterns and create inline decorations
        doc.descendants((node, pos) => {
          if (!node.isText || !node.text) return

          const text = node.text
          let match: RegExpExecArray | null

          // === Standard patterns (highest priority) ===
          // These are the shapes the export strips, so they are 'marker'.

          // Standard tender citations: [1], [2], [10]
          CITATION_REGEX.lastIndex = 0
          while ((match = CITATION_REGEX.exec(text)) !== null) {
            const citationIndex = parseInt(match[1]!, 10)
            if (citationIndex < 1) continue
            addDecoration(
              pos + match.index,
              pos + match.index + match[0].length,
              citationIndex,
              getSectionIdAtPos(pos + match.index),
              'marker'
            )
          }

          // Standard library citations: [L1], [L2], [L10]
          LIBRARY_CITATION_REGEX.lastIndex = 0
          while ((match = LIBRARY_CITATION_REGEX.exec(text)) !== null) {
            const citationIndex = parseInt(match[1]!, 10)
            if (citationIndex < 1) continue
            addDecoration(
              pos + match.index,
              pos + match.index + match[0].length,
              citationIndex,
              getSectionIdAtPos(pos + match.index),
              'marker',
              'library'
            )
          }

          // === Malformed patterns (lower priority, skipped if already decorated) ===
          // All 'loose': the export leaves these brackets in the document, so
          // evaluator view must leave them on screen. `[30 days]` matches the
          // annotated pattern below and is ordinary prose.

          // Combined tender: [1, 3] or [1,3,7] — use first index
          COMBINED_CITATION_REGEX.lastIndex = 0
          while ((match = COMBINED_CITATION_REGEX.exec(text)) !== null) {
            const citationIndex = parseInt(match[1]!, 10)
            if (citationIndex < 1) continue
            addDecoration(
              pos + match.index,
              pos + match.index + match[0].length,
              citationIndex,
              getSectionIdAtPos(pos + match.index),
              'loose'
            )
          }

          // Combined library: [L1, L3] or [L1,L3] — use first index
          COMBINED_LIBRARY_CITATION_REGEX.lastIndex = 0
          while ((match = COMBINED_LIBRARY_CITATION_REGEX.exec(text)) !== null) {
            const citationIndex = parseInt(match[1]!, 10)
            if (citationIndex < 1) continue
            addDecoration(
              pos + match.index,
              pos + match.index + match[0].length,
              citationIndex,
              getSectionIdAtPos(pos + match.index),
              'loose',
              'library'
            )
          }

          // Annotated tender: [1 EMP] or [1 Document Name]
          ANNOTATED_CITATION_REGEX.lastIndex = 0
          while ((match = ANNOTATED_CITATION_REGEX.exec(text)) !== null) {
            const citationIndex = parseInt(match[1]!, 10)
            if (citationIndex < 1) continue
            addDecoration(
              pos + match.index,
              pos + match.index + match[0].length,
              citationIndex,
              getSectionIdAtPos(pos + match.index),
              'loose'
            )
          }

          // Annotated library: [L1 EMP] or [L1 Environmental Management Plan]
          ANNOTATED_LIBRARY_CITATION_REGEX.lastIndex = 0
          while ((match = ANNOTATED_LIBRARY_CITATION_REGEX.exec(text)) !== null) {
            const citationIndex = parseInt(match[1]!, 10)
            if (citationIndex < 1) continue
            addDecoration(
              pos + match.index,
              pos + match.index + match[0].length,
              citationIndex,
              getSectionIdAtPos(pos + match.index),
              'loose',
              'library'
            )
          }
        })

        return DecorationSet.create(doc, decorations)
      },
    },
  })
}
